import * as React from "react";
import { createRef, useState, useMemo } from "react";
import {
  Box,
  Input,
  Table,
  THead,
  TBody,
  Tr,
  Th,
  Td,
  Anchor,
  Button,
  Tooltip,
} from "@twilio-paste/core";
import { Modal, ModalHeader, ModalHeading } from "@twilio-paste/modal";
import { SearchIcon } from "@twilio-paste/icons/esm/SearchIcon";
import { useDispatch, useSelector } from "react-redux";

import { getTranslation } from "../../utils/localUtils";
import { AppState } from "../../store";
import { searchCustomersByCode } from "../../store/action-creators";
import { ActionType } from "../../store/action-types";
import { ConversationContact } from "../../dto/contact";
import { canOpenChatCTA } from "../../utils/canOpenChatCTA";

type CustomerRow = {
  customerCode: string;
  fullName: string;
  number: string;
  conversation: ConversationContact | null;
};

interface CustomerPickerModalProps {
  isOpen: boolean;
  title?: string;
  onDismiss: () => void;
  onChatClick: (row: CustomerRow) => void;

  searchPlaceholder?: string;
  initialQuery?: string;
  isLoading?: boolean;
  footer?: React.ReactNode;
}

const CustomerPickerModal: React.FC<CustomerPickerModalProps> = ({
  isOpen,
  title = "New message",
  onDismiss,
  onChatClick,
  searchPlaceholder = "Cerca per codice cliente",
  initialQuery = "",
  footer,
}) => {
  const dispatch = useDispatch();
  const searchRef = createRef<HTMLInputElement>();
  const [query, setQuery] = useState(initialQuery);

  const local = useSelector((state: AppState) => state.local);
  const operator = useSelector((state: AppState) => state.operator);
  const customerSearch = useSelector((state: AppState) => state.customerSearch);
  const isLoading = customerSearch.isLoading;

  const trimmedQuery = query.trim();
  const canSearch = trimmedQuery.length >= 1;

  const storedQuery = customerSearch.query;

  const rows: CustomerRow[] = useMemo(() => {
    const contact = customerSearch.item; // ContactDto | null
    if (!contact) return [];

    return [
      {
        customerCode: contact.customerCode,
        fullName: [contact.firstName, contact.lastName]
          .filter(Boolean)
          .join(" "),
        number: contact.phoneNumber,
        conversation: contact.conversation,
      },
    ];
  }, [customerSearch.item]);

  const hasUserSearched = (storedQuery ?? "").trim().length > 0;

  const resetResults = () => {
    dispatch({
      type: ActionType.SET_CUSTOMER_SEARCH_RESULTS,
      payload: { query: "", item: null },
    });
  };

  const performSearch = () => {
    if (!canSearch || isLoading) return;

    // 👇 Mettiamo esplicitamente il focus sull'input
    if (searchRef.current) {
      searchRef.current.focus();
    }

    dispatch(searchCustomersByCode(trimmedQuery));
  };

  const handleInputChange = (value: string) => {
    setQuery(value);

    // Se l'input torna vuoto, resettiamo i risultati
    if (value.trim().length === 0) {
      resetResults();
    }
  };

  const handleInputKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (
    e
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      performSearch();
    }
  };

  return (
    <Modal
      ariaLabelledby="customer-picker-heading"
      isOpen={isOpen}
      onDismiss={onDismiss}
      initialFocusRef={searchRef}
      size="default"
    >
      <ModalHeader>
        <ModalHeading as="h3" id="customer-picker-heading">
          {title}
        </ModalHeading>
      </ModalHeader>

      <Box paddingX="space70" paddingBottom="space70" paddingTop="space40">
        {/* Search row: input + bottone "Cerca" */}
        <Box
          marginBottom="space60"
          display="flex"
          columnGap="space30"
          alignItems="center"
        >
          <Box flex="1 1 auto" minWidth={0}>
            <Input
              id="customer-search"
              name="customer-search"
              type="text"
              value={query}
              onChange={(e) => handleInputChange(e.currentTarget.value)}
              onKeyDown={handleInputKeyDown}
              placeholder={searchPlaceholder}
              insertBefore={<SearchIcon decorative size="sizeIcon20" />}
              ref={searchRef}
            />
          </Box>

          <Button
            variant="secondary"
            onClick={performSearch}
            disabled={isLoading || !canSearch}
          >
            Cerca
          </Button>
        </Box>

        {/* Results table */}
        <Box overflowX="auto">
          <Table tableLayout="auto">
            <THead>
              <Tr>
                <Th>CustomerCode</Th>
                <Th>Nome</Th>
                <Th>Numero</Th>
                <Th>Team</Th>
                <Th textAlign="right">Azioni</Th>
              </Tr>
            </THead>
            <TBody>
              {isLoading ? (
                <Tr>
                  <Td colSpan={4}>{getTranslation(local, "convoLoading")}</Td>
                </Tr>
              ) : rows.length === 0 && hasUserSearched ? (
                <Tr>
                  <Td colSpan={4}>{getTranslation(local, "noResults")}</Td>
                </Tr>
              ) : rows.length === 0 && !hasUserSearched ? (
                <Tr>
                  <Td colSpan={4}>
                    {getTranslation(local, "startTypingCustomerCode") ??
                      "Inserisci un codice cliente e premi Cerca."}
                  </Td>
                </Tr>
              ) : (
                rows.map((row) => {
                  const canChat = canOpenChatCTA(row.conversation, {
                    id: operator?.id,
                    teamId: operator?.teamId,
                  });

                  return (
                    <Tr key={`${row.customerCode}-${row.number}`}>
                      <Td>
                        <Box
                          as="span"
                          display="inline-block"
                          paddingX="space30"
                          paddingY="space10"
                          borderColor="colorBorderWeaker"
                          borderStyle="solid"
                          borderWidth="borderWidth10"
                          borderRadius="borderRadius30"
                        >
                          {row.customerCode}
                        </Box>
                      </Td>
                      <Td>{row.fullName}</Td>
                      <Td>{row.number}</Td>
                      <Td>{row.conversation?.teamName ?? "-"}</Td>
                      <Td textAlign="center">
                        {canChat ? (
                          <Anchor
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              onChatClick(row);
                            }}
                          >
                            CHAT
                          </Anchor>
                        ) : (
                          <Tooltip
                            text={"Chat non disponibile."}
                            placement={"bottom-start"}
                          >
                            <p>-</p>
                          </Tooltip>
                        )}
                      </Td>
                    </Tr>
                  );
                })
              )}
            </TBody>
          </Table>
        </Box>
      </Box>

      {footer ? (
        <Box paddingX="space70" paddingBottom="space70">
          {footer}
        </Box>
      ) : null}
    </Modal>
  );
};

export default CustomerPickerModal;
